/**
 * Manual commit ↔ task links (commit ↔ task links, slice 2). Pure.
 *
 * Automatic links stay derived from the commit text (commitTaskRefs.js). What
 * a person adds or removes by hand is stored ON THE TASK as `commitLinks`,
 * the same place a task keeps its comments: the task record is spread, so an
 * older app keeps the field through its own saves, and the link syncs with
 * the board to the user's other devices and the team.
 *
 *   { sha, repoId, repoName, summary, author, time, branch, remoteUrl,
 *     provider, linkedBy, linkedAt }                 a commit linked by hand
 *   { sha, repoId, ..., hidden: true }               an automatic link removed
 *
 * Unlinking a hand-made link drops its entry; unlinking an automatic one
 * stores it as hidden (Linear's "unlink" on an auto-linked commit), so the
 * commit message cannot bring it back. "Link again" drops the hidden entry.
 */

export const MAX_COMMIT_LINKS = 100;
const MAX_SUMMARY = 200;

const sameCommit = (a, b) => String(a?.sha || a?.oid || '') === String(b?.sha || b?.oid || '') && String(a?.repoId ?? '') === String(b?.repoId ?? '');

/** The task's stored entries, junk dropped. */
export function commitLinksOf(task) {
  return (Array.isArray(task?.commitLinks) ? task.commitLinks : []).filter((l) => l && /^[0-9a-f]{7,64}$/i.test(String(l.sha || '')));
}

/**
 * The links as they may leave the app over MCP (perimeter rule MCP 8: no
 * remote URL, owner, repository or account leaves): the commit id, its
 * message line, author, time, branch and the flags — never `remoteUrl`,
 * `repoId`, `repoName` or `provider`.
 */
export function publicCommitLinks(task) {
  return commitLinksOf(task).map((l) => {
    const out = { sha: l.sha, summary: l.summary || '', author: l.author || '', time: l.time ?? null, branch: l.branch || '' };
    if (l.hidden) out.hidden = true;
    if (l.closed) out.closed = true;
    if (l.linkedVia) out.linkedVia = l.linkedVia;
    if (l.linkedAt) out.linkedAt = l.linkedAt;
    return out;
  });
}

/** A link entry for `commit` (a code_repo_commits row, maybe with `repo`). */
export function linkEntryFor(commit, { repo = commit?.repo, userId = null, now = () => new Date().toISOString() } = {}) {
  const sha = String(commit?.sha || commit?.oid || '');
  return {
    sha,
    repoId: repo?.id != null ? String(repo.id) : commit?.repoId != null ? String(commit.repoId) : null,
    repoName: repo?.name || commit?.repoName || '',
    summary: String(commit?.summary || '').slice(0, MAX_SUMMARY),
    author: commit?.author || '',
    time: Number.isFinite(commit?.time) ? commit.time : null,
    branch: commit?.branch || '',
    remoteUrl: repo?.remote || commit?.remoteUrl || '',
    provider: repo?.provider || commit?.provider || '',
    linkedBy: userId != null ? String(userId) : null,
    linkedAt: now(),
  };
}

/** The task's `commitLinks` after linking `commit` by hand (a hidden entry for it is replaced). */
export function withCommitLinked(task, commit, opts = {}) {
  const entry = linkEntryFor(commit, opts);
  // A close marker (`auto`, slice 3) stays: it is what stops a commit closing the task twice.
  const rest = commitLinksOf(task).filter((l) => l.auto || !sameCommit(l, entry));
  return [...rest, entry].slice(-MAX_COMMIT_LINKS);
}

/**
 * The task's `commitLinks` after unlinking `commit`. `automatic` = the commit
 * is linked by its message or branch, so only a hidden entry removes it.
 */
export function withCommitUnlinked(task, commit, { automatic = false, userId = null, now } = {}) {
  const links = commitLinksOf(task);
  const rest = links.filter((l) => l.auto || !sameCommit(l, { sha: commit?.sha || commit?.oid, repoId: commit?.repoId ?? commit?.repo?.id }));
  if (!automatic) return rest;
  return [...rest, { ...linkEntryFor(commit, { userId, now }), hidden: true }].slice(-MAX_COMMIT_LINKS);
}

/** The task's `commitLinks` after "Link again" on a hidden automatic link. */
export function withCommitUnhidden(task, commit) {
  return commitLinksOf(task).filter((l) => l.auto || !(l.hidden && sameCommit(l, { sha: commit?.sha || commit?.oid, repoId: commit?.repoId ?? commit?.repo?.id })));
}

/**
 * What the task shows: automatic links minus the hidden ones, plus the
 * hand-made ones (an entry already found automatically is marked `manual`
 * too, never listed twice). A hand-made link to a commit not in any local log
 * (another device's clone, a pruned branch) shows from its stored snapshot.
 * `derived` rows carry `repo` (and `repoId` via repo.id); `known` maps
 * `<repoId>:<sha>` to a row of the local logs, so a hand-made link to a commit
 * in a log shows its current data rather than the snapshot.
 *
 * @returns {{ shown: Array, hidden: Array }}
 */
export function mergeTaskCommits(derived, links, { reposById = new Map(), known = new Map() } = {}) {
  const entries = Array.isArray(links) ? links : [];
  const keyOf = (c) => `${c.repoId ?? c.repo?.id ?? ''}:${c.sha || c.oid}`;
  const hiddenKeys = new Set(entries.filter((l) => l.hidden).map(keyOf));
  // `auto` entries are close markers (slice 3), not hand-made links.
  const manualKeys = new Set(entries.filter((l) => !l.hidden && !l.auto).map(keyOf));
  const closedShas = new Set(entries.filter((l) => l.closed).map((l) => String(l.sha)));
  const shown = [];
  const hidden = [];
  const seen = new Set();
  for (const c of derived || []) {
    const k = keyOf(c);
    seen.add(k);
    if (hiddenKeys.has(k)) hidden.push({ ...c, repoId: c.repo?.id ?? c.repoId ?? null });
    else shown.push({ ...c, repoId: c.repo?.id ?? c.repoId ?? null, manual: manualKeys.has(k), closedTask: closedShas.has(String(c.sha || c.oid)) });
  }
  for (const l of entries) {
    if (l.hidden || seen.has(keyOf(l))) continue;
    // A close marker whose commit is in no local log (another device's clone): shown from its snapshot.
    if (l.auto) {
      if (hiddenKeys.has(keyOf(l)) || manualKeys.has(keyOf(l))) continue;
      seen.add(keyOf(l));
      const repo = reposById.get(String(l.repoId)) || null;
      shown.push({ oid: l.sha, sha: l.sha, summary: l.summary, author: l.author, time: l.time, branch: l.branch, onDefault: true, repoId: l.repoId, repo: repo || { id: l.repoId, name: '' }, remoteUrl: repo?.remote || '', linkedBy: 'message', manual: false, closedTask: true });
      continue;
    }
    // In a local log after all (a commit that does not name the key): show it fresh.
    const live = known.get(keyOf(l));
    if (live) {
      shown.push({ ...live, repoId: live.repo?.id ?? l.repoId, linkedBy: 'manual', manual: true, closedTask: closedShas.has(String(l.sha)) });
      continue;
    }
    const repo = reposById.get(String(l.repoId)) || null;
    shown.push({
      oid: l.sha,
      sha: l.sha,
      summary: l.summary,
      author: l.author,
      time: l.time,
      branch: l.branch,
      onDefault: false,
      repoId: l.repoId,
      repo: repo || { id: l.repoId, name: l.repoName, remote: l.remoteUrl, provider: l.provider },
      remoteUrl: repo?.remote || l.remoteUrl || '',
      linkedBy: 'manual',
      manual: true,
    });
  }
  shown.sort((a, b) => (b.time || 0) - (a.time || 0));
  return { shown, hidden };
}
